import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Download, RefreshCw, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGINA = 100;

const EVENTI = [['', 'Tutti gli eventi'], ['login_ok', 'Accessi'], ['login_fail', 'Accessi falliti'], ['login_blocked', 'Accessi bloccati'],
  ['uso', "Uso dell'app"], ['uso_come', 'Amministratore entrato come condomino']];
const BADGE = { login_ok: 'bg-green-100 text-green-800', login_fail: 'bg-red-100 text-red-800', login_blocked: 'bg-red-100 text-red-800',
  uso: 'bg-gray-100 text-gray-700', uso_come: 'bg-blue-100 text-blue-800' };
const STILE_AVVISO = { tentativi: 'border-red-300 bg-red-50', bloccato: 'border-red-300 bg-red-50', ip_nuovo: 'border-amber-300 bg-amber-50', molti_ip: 'border-amber-300 bg-amber-50' };
const VUOTO = { evento: '', chi: '', ip: '', dal: '', al: '', solo_nuovi: false };

const quando = (iso) => { try { return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' }); } catch { return iso; } };
const query = (f, extra = {}) => new URLSearchParams(Object.entries({ ...f, ...extra })
  .filter(([, v]) => v !== '' && v !== false && v != null).map(([k, v]) => [k, String(v)])).toString();

// Registro accessi: chi entra nell'app, quando e da quale indirizzo IP. Solo lettura: non invia nulla a nessuno.
export const RegistroAccessi = () => {
  const { token } = useAuth();
  const intest = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [filtri, setFiltri] = useState(VUOTO);
  const [applicati, setApplicati] = useState(VUOTO);
  const [righe, setRighe] = useState([]);
  const [totale, setTotale] = useState(0);
  const [riepilogo, setRiepilogo] = useState(null);
  const [occupato, setOccupato] = useState(false);

  const carica = useCallback(async (f, salta = 0) => {
    setOccupato(true);
    try {
      const res = await fetch(`${API_URL}/api/registro-accessi?${query(f, { limite: PAGINA, salta })}`, { headers: intest });
      const dati = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof dati.detail === 'string' ? dati.detail : 'Registro non disponibile');
      setTotale(dati.totale);
      setRighe((cur) => (salta ? [...cur, ...dati.righe] : dati.righe));
    } catch (e) { toast.error(e.message); } finally { setOccupato(false); }
  }, [intest]);

  const caricaRiepilogo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/registro-accessi/riepilogo`, { headers: intest });
      if (res.ok) setRiepilogo(await res.json());
    } catch { /* il riepilogo e' un di piu': la tabella funziona anche senza */ }
  }, [intest]);

  useEffect(() => { carica(VUOTO); caricaRiepilogo(); }, [carica, caricaRiepilogo]);

  const applica = (f) => { setFiltri(f); setApplicati(f); carica(f); };

  const scaricaCsv = async () => {
    try {
      const res = await fetch(`${API_URL}/api/registro-accessi/esporta.csv?${query(applicati)}`, { headers: intest });
      if (!res.ok) throw new Error('Esportazione non riuscita');
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = 'registro-accessi.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const n24 = (k) => (riepilogo ? riepilogo.ultime_24_ore.eventi[k] || 0 : '-');

  return (
    <div className="space-y-4" data-testid="registro-accessi-page">
      <PageHeader title="Registro accessi" description="Chi entra nell'app, quando e da quale indirizzo IP. Si conserva per 400 giorni. Non contiene mai password né codici." />

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => { carica(applicati); caricaRiepilogo(); }}><RefreshCw className="h-4 w-4 mr-1" /> Aggiorna</Button>
        <Button variant="outline" size="sm" onClick={scaricaCsv}><Download className="h-4 w-4 mr-1" /> Scarica CSV</Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[['Accessi, ultime 24 ore', n24('login_ok')],
          ['Accessi falliti o bloccati', riepilogo ? n24('login_fail') + n24('login_blocked') : '-'],
          ['Indirizzi IP diversi', riepilogo ? riepilogo.ultime_24_ore.ip : '-'],
          ['Persone attive', riepilogo ? riepilogo.ultime_24_ore.persone : '-']].map(([l, v]) => (
          <Card key={l}><CardContent className="pt-4"><p className="text-3xl font-bold">{v}</p><p className="text-sm text-muted-foreground">{l}</p></CardContent></Card>))}
      </div>

      {riepilogo && riepilogo.avvisi.length > 0 && (
        <Card className="border-amber-300">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ShieldAlert className="h-5 w-5 text-amber-700" /> Da guardare</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {riepilogo.avvisi.map((a, i) => (
              <button key={i} type="button" className={`flex w-full items-center justify-between gap-3 rounded border p-3 text-left text-sm ${STILE_AVVISO[a.tipo] || ''}`}
                onClick={() => applica({ ...VUOTO, ...(a.ip ? { ip: a.ip } : { chi: a.email || '' }) })}>
                <span>{a.testo}</span><span className="shrink-0 text-xs text-muted-foreground">{a.quando ? quando(a.quando) : ''}</span>
              </button>))}
          </CardContent>
        </Card>)}

      <Card>
        <CardContent className="space-y-3 pt-4">
          <div className="grid gap-2 md:grid-cols-6">
            <select className="h-9 rounded-md border bg-background px-2 text-sm md:col-span-2" value={filtri.evento} onChange={(e) => setFiltri({ ...filtri, evento: e.target.value })}>
              {EVENTI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input placeholder="Persona (nome o email)" value={filtri.chi} onChange={(e) => setFiltri({ ...filtri, chi: e.target.value })} />
            <Input placeholder="Indirizzo IP" value={filtri.ip} onChange={(e) => setFiltri({ ...filtri, ip: e.target.value })} />
            <Input type="date" aria-label="Dal" value={filtri.dal} onChange={(e) => setFiltri({ ...filtri, dal: e.target.value })} />
            <Input type="date" aria-label="Al" value={filtri.al} onChange={(e) => setFiltri({ ...filtri, al: e.target.value })} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={filtri.solo_nuovi} onChange={(e) => setFiltri({ ...filtri, solo_nuovi: e.target.checked })} /> Solo accessi da un indirizzo nuovo
            </label>
            <Button size="sm" onClick={() => applica(filtri)}>Applica</Button>
            <Button size="sm" variant="ghost" onClick={() => applica(VUOTO)}>Azzera</Button>
            <span className="ml-auto text-sm text-muted-foreground">{totale} righe</span>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data e ora</TableHead><TableHead>Evento</TableHead><TableHead>Persona</TableHead>
              <TableHead>Indirizzo IP</TableHead><TableHead>Dispositivo</TableHead><TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {righe.length === 0 && (
              <TableRow><TableCell colSpan={6} className="p-6 text-center text-muted-foreground">{occupato ? 'Carico…' : 'Nessuna riga con questi filtri.'}</TableCell></TableRow>)}
            {righe.map((r) => (
              <TableRow key={r.id} className="align-top">
                <TableCell className="whitespace-nowrap">{quando(r.at)}</TableCell>
                <TableCell><Badge variant="outline" className={BADGE[r.evento] || ''}>{r.etichetta}</Badge></TableCell>
                <TableCell><span className="font-medium">{r.nome || '—'}</span><span className="block text-xs text-muted-foreground">{r.email}</span></TableCell>
                <TableCell><button type="button" className="font-mono text-blue-800 hover:underline" onClick={() => applica({ ...VUOTO, ip: r.ip })}>{r.ip}</button></TableCell>
                <TableCell>{r.browser} · {r.sistema}<span className="block text-xs text-muted-foreground">{r.dispositivo}</span></TableCell>
                <TableCell>
                  {r.ip_nuovo && <Badge variant="outline" className="mr-1 bg-amber-100 text-amber-800">IP nuovo</Badge>}
                  <span className="text-xs text-muted-foreground">{r.dettaglio}</span>
                </TableCell>
              </TableRow>))}
          </TableBody>
        </Table>
      </div>
      {righe.length < totale && (
        <div className="text-center"><Button variant="outline" disabled={occupato} onClick={() => carica(applicati, righe.length)}>Carica altre righe</Button></div>)}

      {riepilogo && riepilogo.ip_piu_attivi.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Indirizzi più attivi (ultimi 7 giorni)</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {riepilogo.ip_piu_attivi.map((t) => (
              <button key={t.ip} type="button" className="rounded border px-3 py-1 font-mono text-sm hover:bg-muted" onClick={() => applica({ ...VUOTO, ip: t.ip })}>{t.ip} · {t.eventi}</button>))}
          </CardContent>
        </Card>)}
    </div>
  );
};
